import { NgFor, NgIf } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatIconButton } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormField, MatError } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { finalize } from "rxjs/operators";
import { ConfiguracionGeneral } from "src/app/shared/model/emision/configuracionGeneral.model";
import { ConfiguracionService } from "../configuracion.service";
import {
  ConfiguracionNoticeDialogComponent,
  ConfiguracionNoticeDialogData,
} from "../configuracion-notice-dialog/configuracion-notice-dialog.component";

interface ConfiguracionParameterPair {
  descriptionControl: string;
  valueControl: string;
}

export interface ConfiguracionFormDialogResult {
  saved: true;
  primaryKey: string;
}

@Component({
  selector: "app-modal-agregar-config-sec",
  standalone: true,
  templateUrl: "./modal-agregar-config-sec.component.html",
  styleUrls: ["./modal-agregar-config-sec.component.css"],
  imports: [
    NgFor,
    NgIf,
    ReactiveFormsModule,
    MatDialogContent,
    MatDialogActions,
    MatFormField,
    MatInput,
    MatError,
    MatIcon,
    MatIconButton,
  ],
})
export class ModalAgregarConfigSecComponent implements OnInit {
  readonly parameterPairs: ConfiguracionParameterPair[] = [
    { descriptionControl: "descripcion1Ctrl", valueControl: "valor1Ctrl" },
    { descriptionControl: "descripcion2Ctrl", valueControl: "valor2Ctrl" },
    { descriptionControl: "descripcion3Ctrl", valueControl: "valor3Ctrl" },
    { descriptionControl: "descripcion4Ctrl", valueControl: "valor4Ctrl" },
  ];

  formVersion: FormGroup;
  ConfiguracionSec: ConfiguracionGeneral = new ConfiguracionGeneral();
  isEditMode = false;
  isSaving = false;

  constructor(
    public readonly dialogRef: MatDialogRef<
      ModalAgregarConfigSecComponent,
      ConfiguracionFormDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public readonly data: string | ConfiguracionGeneral,
    private readonly formBuilder: FormBuilder,
    private readonly confService: ConfiguracionService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initializeConfiguration();
    this.initializeForm();
    this.parameterPairs.forEach(pair => this.updatePairValidation(pair));
  }

  clear(): void {
    if (this.isSaving) {
      return;
    }

    this.formVersion.reset({
      nameVersionCtrl: "",
      descripcion1Ctrl: "",
      valor1Ctrl: "",
      descripcion2Ctrl: "",
      valor2Ctrl: "",
      descripcion3Ctrl: "",
      valor3Ctrl: "",
      descripcion4Ctrl: "",
      valor4Ctrl: "",
    });
    this.parameterPairs.forEach(pair => this.updatePairValidation(pair));
  }

  onNoClick(): void {
    if (!this.isSaving) {
      this.dialogRef.close();
    }
  }

  save(): void {
    if (this.isSaving) {
      return;
    }

    if (this.formVersion.invalid) {
      this.formVersion.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const configuration = this.buildConfiguration();

    this.confService
      .saveConf(configuration)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: response => {
          if (response.headers.get("COD_STATUS") !== null) {
            this.openErrorDialog(
              "No fue posible guardar la configuración. Revise los datos e intente nuevamente."
            );
            return;
          }

          this.dialogRef.close({
            saved: true,
            primaryKey: configuration.conf_fol_pr,
          });
        },
        error: () => {
          this.openErrorDialog(
            "No se pudo comunicar con el servidor. Intente nuevamente en unos momentos."
          );
        },
      });
  }

  updatePairValidation(pair: ConfiguracionParameterPair): void {
    if (!this.formVersion) {
      return;
    }

    const descriptionControl = this.formVersion.controls[pair.descriptionControl];
    const valueControl = this.formVersion.controls[pair.valueControl];
    const isFirstPair = pair === this.parameterPairs[0];
    const hasDescription = Boolean(String(descriptionControl.value || "").trim());
    const hasValue = Boolean(String(valueControl.value || "").trim());
    const pairIsRequired = isFirstPair || hasDescription || hasValue;

    if (pairIsRequired) {
      descriptionControl.setValidators([Validators.required]);
      valueControl.setValidators([Validators.required]);
    } else {
      descriptionControl.clearValidators();
      valueControl.clearValidators();
    }

    descriptionControl.updateValueAndValidity({ emitEvent: false });
    valueControl.updateValueAndValidity({ emitEvent: false });
  }

  private initializeConfiguration(): void {
    if (typeof this.data === "string") {
      this.ConfiguracionSec = new ConfiguracionGeneral();
      this.ConfiguracionSec.conf_fol_pr = this.data;
      return;
    }

    this.isEditMode = true;
    this.ConfiguracionSec = Object.assign(
      new ConfiguracionGeneral(),
      this.data || {}
    );
  }

  private initializeForm(): void {
    this.formVersion = this.formBuilder.group({
      nameVersionCtrl: [
        this.ConfiguracionSec.conf_fol_se_dsc || "",
        Validators.required,
      ],
      descripcion1Ctrl: [this.ConfiguracionSec.desc01 || "", Validators.required],
      valor1Ctrl: [this.ConfiguracionSec.valor1 || "", Validators.required],
      descripcion2Ctrl: [this.ConfiguracionSec.desc02 || ""],
      valor2Ctrl: [this.ConfiguracionSec.valor2 || ""],
      descripcion3Ctrl: [this.ConfiguracionSec.desc03 || ""],
      valor3Ctrl: [this.ConfiguracionSec.valor3 || ""],
      descripcion4Ctrl: [this.ConfiguracionSec.desc04 || ""],
      valor4Ctrl: [this.ConfiguracionSec.valor4 || ""],
    });
  }

  private buildConfiguration(): ConfiguracionGeneral {
    const value = this.formVersion.getRawValue();
    const configuration = Object.assign(
      new ConfiguracionGeneral(),
      this.ConfiguracionSec
    );

    configuration.conf_fol_se_dsc = this.normalize(value.nameVersionCtrl);
    configuration.desc01 = this.normalize(value.descripcion1Ctrl);
    configuration.valor1 = this.normalize(value.valor1Ctrl);
    configuration.desc02 = this.normalize(value.descripcion2Ctrl);
    configuration.valor2 = this.normalize(value.valor2Ctrl);
    configuration.desc03 = this.normalize(value.descripcion3Ctrl);
    configuration.valor3 = this.normalize(value.valor3Ctrl);
    configuration.desc04 = this.normalize(value.descripcion4Ctrl);
    configuration.valor4 = this.normalize(value.valor4Ctrl);

    return configuration;
  }

  private normalize(value: unknown): string {
    const normalized = String(value || "").trim();
    return normalized
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
      : "";
  }

  private openErrorDialog(message: string): void {
    const data: ConfiguracionNoticeDialogData = {
      tone: "error",
      eyebrow: "No se completó la operación",
      title: "Ocurrió un problema",
      message,
      confirmText: "Entendido",
    };

    this.dialog.open(ConfiguracionNoticeDialogComponent, {
      autoFocus: "first-tabbable",
      disableClose: true,
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      width: "440px",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      ariaLabel: data.title,
      data,
    });
  }
}
